import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from './Toast';

export default function PartsTab({ claim, session, room, onRefresh, onLocal, tl }) {
  const tlBg = tl ? 'var(--sp-bg)' : 'var(--bg2)';
  const tlBg2 = tl ? 'var(--sp-bg2)' : 'var(--bg3)';
  const tlBorder = tl ? 'var(--sp-border)' : 'var(--border)';
  const tlInput = tl ? { background: 'var(--sp-bg2)', borderColor: 'var(--sp-border)' } : {};
  const [adding, setAdding] = useState(false);
  const [newPart, setNewPart] = useState({ name: '', part_number: '', price: '' });
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [removingId, setRemovingId] = useState(null); // shrink-out animation before delete

  const parts = claim.parts || [];
  const arrived = parts.filter(p => p.arrived).length;

  // Optimistic-UI: mutate the parent's claim.parts immediately; the realtime
  // subscription in ClaimModal reconciles the true state afterwards.
  const mutate = onLocal || (() => onRefresh());

  async function togglePart(part) {
    // Optimistic: flip the checkbox immediately, revert if the write fails
    mutate(c => ({ ...c, parts: (c.parts || []).map(p => (p.id === part.id ? { ...p, arrived: !part.arrived } : p)) }));
    const { error } = await supabase.from('parts').update({ arrived: !part.arrived }).eq('id', part.id);
    if (error) {
      mutate(c => ({ ...c, parts: (c.parts || []).map(p => (p.id === part.id ? { ...p, arrived: part.arrived } : p)) }));
      toast('Could not update the part — check your connection and try again.', 'error');
      return;
    }
    if (!part.arrived) {
      supabase.from('activity_log').insert({
        room_id: room.id,
        claim_id: claim.id,
        user_id: session.user.id,
        user_name: session.user.user_metadata?.full_name || session.user.email,
        action: 'part_arrived',
        detail: `marked "${part.name}" as arrived on ${claim.car || 'claim'}`,
      }).then(() => {});
    }
  }

  async function addPart(e) {
    e.preventDefault();
    if (!newPart.name.trim()) return;
    const row = {
      claim_id: claim.id,
      name: newPart.name.trim(),
      part_number: newPart.part_number.trim() || null,
      price: newPart.price ? parseFloat(newPart.price) : null,
    };
    // Optimistic: show the part and reset the form immediately
    const temp = { ...row, id: `temp-${Date.now()}`, arrived: false };
    const prevForm = newPart;
    mutate(c => ({ ...c, parts: [...(c.parts || []), temp] }));
    setNewPart({ name: '', part_number: '', price: '' });
    setAdding(false);
    const { data, error } = await supabase.from('parts').insert(row).select().single();
    if (error) {
      mutate(c => ({ ...c, parts: (c.parts || []).filter(p => p.id !== temp.id) }));
      setNewPart(prevForm);
      setAdding(true);
      toast('Could not add the part — check your connection and try again.', 'error');
      return;
    }
    mutate(c => ({ ...c, parts: (c.parts || []).map(p => (p.id === temp.id ? data : p)) }));
  }

  async function deletePart(id) {
    if (!window.confirm('Delete this part?')) return;
    // Shrink the row away, then remove it
    setRemovingId(id);
    await new Promise(r => setTimeout(r, 180));
    setRemovingId(null);
    const removed = parts.find(p => p.id === id);
    // Optimistic: remove immediately, restore if the delete fails
    mutate(c => ({ ...c, parts: (c.parts || []).filter(p => p.id !== id) }));
    const { error } = await supabase.from('parts').delete().eq('id', id);
    if (error && removed) {
      mutate(c => ({ ...c, parts: [...(c.parts || []), removed] }));
      toast('Could not delete the part — check your connection and try again.', 'error');
    }
  }

  async function handlePdfUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setParsing(true);

    try {
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      let fullText = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        fullText += content.items.map(item => item.str).join(' ') + '\n';
      }

      // Parse parts from text - look for part numbers and names
      const lines = fullText.split('\n');
      const parsedParts = [];

      for (const line of lines) {
        // Match patterns like part numbers followed by descriptions and prices
        const priceMatch = line.match(/\$?([\d,]+\.?\d*)/);
        const price = priceMatch ? parseFloat(priceMatch[1].replace(',', '')) : null;

        // Skip lines that are too short or are headers
        if (line.trim().length < 5) continue;
        if (/total|subtotal|tax|labor|estimate|page|date/i.test(line)) continue;

        // Look for lines that could be parts (have some alphanumeric content)
        if (/[A-Z0-9]{4,}/i.test(line) && line.trim().length > 8) {
          const name = line.replace(/\$[\d,]+\.?\d*/g, '').trim().substring(0, 80);
          if (name.length > 4) {
            parsedParts.push({ name, price });
          }
        }
      }

      // Insert all parsed parts
      const toInsert = parsedParts.map(p => ({
        claim_id: claim.id,
        name: p.name,
        price: p.price,
        arrived: false,
      }));

      if (toInsert.length > 0) {
        const { data, error } = await supabase.from('parts').insert(toInsert).select();
        if (error) throw error;
        mutate(c => ({ ...c, parts: [...(c.parts || []), ...(data || [])] }));
        toast(`Imported ${toInsert.length} parts from PDF. Review and clean up as needed.`, 'success');
      } else {
        toast('Could not auto-detect parts from this PDF. Try adding them manually.', 'error');
      }
    } catch (err) {
      console.error(err);
      toast('Error reading PDF. Try adding parts manually.', 'error');
    }

    setParsing(false);
    e.target.value = '';
  }

  const partsStatusColor = arrived === parts.length && parts.length > 0 ? 'var(--green)' :
    arrived > 0 ? 'var(--yellow)' : 'var(--red)';
  const partsStatusLabel = parts.length === 0 ? 'No Parts' :
    arrived === parts.length ? 'All In' : arrived > 0 ? 'Partial' : 'Waiting';

  return (
    <div style={{ padding: 16 }}>
      {/* Status */}
      {parts.length > 0 && (
        <div style={{
          padding: '10px 14px',
          background: 'var(--bg2)',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 13, color: 'var(--text2)' }}>Parts Status</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: partsStatusColor }}>
            {partsStatusLabel} ({arrived}/{parts.length})
          </span>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <label className="raise-3d" style={{
          flex: 1, padding: '9px 12px',
          background: tlBg2, border: tl ? '1px solid var(--sp-border)' : 'none',
          borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 600,
          color: 'var(--text2)', cursor: 'pointer', textAlign: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
          {parsing ? <div className="spinner" style={{ width: 16, height: 16 }} /> : '📄 Import PDF'}
          <input type="file" accept=".pdf" onChange={handlePdfUpload} style={{ display: 'none' }} disabled={parsing} />
        </label>
        <button
          className="btn btn-primary"
          onClick={() => setAdding(true)}
          style={{ flex: 1, fontSize: 13, padding: '9px 12px' }}
        >
          + Add Part
        </button>
      </div>

      {/* Add part form */}
      {adding && (
        <form onSubmit={addPart} style={{ background: tlBg, border: `1px solid ${tlBorder}`, borderRadius: 'var(--radius-sm)', padding: 14, marginBottom: 14 }}>
          <div className="field">
            <label className="label">Part Name</label>
            <input value={newPart.name} onChange={e => setNewPart(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Front Bumper Assembly" autoFocus required style={tlInput} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="field">
              <label className="label">Part #</label>
              <input value={newPart.part_number} onChange={e => setNewPart(p => ({ ...p, part_number: e.target.value }))} placeholder="Optional" style={{ fontFamily: 'var(--font-mono)', ...tlInput }} />
            </div>
            <div className="field">
              <label className="label">Price</label>
              <input value={newPart.price} onChange={e => setNewPart(p => ({ ...p, price: e.target.value }))} placeholder="0.00" type="number" step="0.01" style={tlInput} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setAdding(false)} style={{ flex: 1 }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ flex: 2 }}>
              {saving ? <div className="spinner" /> : 'Add Part'}
            </button>
          </div>
        </form>
      )}

      {/* Parts list */}
      {parts.length === 0 ? (
        <div className="empty-state">
          <div className="icon">📦</div>
          <div className="title">No parts yet</div>
          <div className="sub">Import from a PDF estimate or add manually</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {parts.map(part => (
            <div key={part.id} className={removingId === part.id ? 'removing' : undefined} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px',
              background: part.arrived ? 'rgba(34,197,94,0.08)' : tlBg,
              border: '1px solid',
              borderColor: part.arrived ? 'rgba(34,197,94,0.2)' : tlBorder,
              borderRadius: 'var(--radius-sm)',
            }}>
              <button
                onClick={() => togglePart(part)}
                style={{
                  width: 22, height: 22, borderRadius: 5, flexShrink: 0,
                  border: `2px solid ${part.arrived ? 'var(--green)' : 'var(--border)'}`,
                  background: part.arrived ? 'var(--green)' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: 13, fontWeight: 700,
                }}
              >
                {part.arrived ? '✓' : ''}
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, color: part.arrived ? 'var(--text2)' : 'var(--text)', textDecoration: part.arrived ? 'line-through' : 'none' }}>
                  {part.name}
                </div>
                {part.part_number && (
                  <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text3)' }}>{part.part_number}</div>
                )}
              </div>
              {part.price && (
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text2)', flexShrink: 0 }}>
                  ${Number(part.price).toFixed(2)}
                </div>
              )}
              {room.role === 'admin' && (
                <button onClick={() => deletePart(part.id)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0 }}>
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
